import React, { useState, useRef, useEffect } from 'react';

const CustomSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select...", 
  className = "",
  optionClassName = "",
  disabled = false,
  displayKey = "name", // Key to display in options
  valueKey = "name", // Key to use as value
  renderOption = null, // Custom render function for options
  renderSelected = null // Custom render function for selected value
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Find the selected option object
  const selectedOption = options.find(option => 
    option[valueKey] === value || 
    (typeof option === 'string' && option === value)
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    const optionValue = typeof option === 'string' ? option : option[valueKey];
    setIsOpen(false);
    onChange(optionValue, option);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const getDisplayText = () => {
    if (renderSelected && selectedOption) {
      return renderSelected(selectedOption);
    }
    
    if (selectedOption) {
      return typeof selectedOption === 'string' 
        ? selectedOption 
        : selectedOption[displayKey];
    }
    
    return placeholder;
  };

  const defaultClassName = "bg-ag-black-800 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-ag-green text-white";
  const defaultOptionClassName = "w-full px-2 py-1 text-xs text-left hover:bg-gray-200 hover:text-black transition-colors duration-150 focus:outline-none focus:bg-ag-green focus:text-black cursor-pointer";

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Select Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`${defaultClassName} ${className} flex items-center justify-between w-full ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span className="truncate">{getDisplayText()}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M19 9l-7 7-7-7" 
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-ag-black-800 border border-gray-300 rounded shadow-lg max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-2 py-1 text-xs text-gray-500">
              No options available
            </div>
          ) : (
            options.map((option, index) => {
              const optionValue = typeof option === 'string' ? option : option[valueKey];
              
              return (
                <button
                  key={`${optionValue}-${index}`}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`${defaultOptionClassName} ${optionClassName}`}
                >
                  {renderOption 
                    ? renderOption(option)
                    : (typeof option === 'string' ? option : option[displayKey])
                  }
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;